import React, { useEffect, useState } from "react"
import { readComponentHistory } from "../api"

const barColor = (v) => {
  if (v === null || v === undefined) return "bg-canvas border border-bordered"
  if (v >= 0.999) return "bg-green-500"
  if (v >= 0.8) return "bg-amber-500"
  return "bg-red-500"
}

const DAY_MS = 24 * 60 * 60 * 1000
const formatDowntime = (v) => {
  if (v == null) return "no data"
  if (v >= 0.999) return "No Downtime"
  const downMs = Math.max(0, (1 - v) * DAY_MS)
  const totalMin = Math.round(downMs / 60_000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0 && m > 0) return `${h}h ${m} min downtime`
  if (h > 0) return `${h}h downtime`
  return `${m} min downtime`
}

const aggregateColor = (buckets) => {
  if (!buckets || buckets.length === 0) return "text-neutral-500"
  const todayVal = buckets[buckets.length - 1]?.value
  if (todayVal === null || todayVal === undefined) return "text-neutral-500"
  if (todayVal >= 0.999) return "text-green-600"
  if (todayVal >= 0.8) return "text-amber-600"
  return "text-red-600"
}

const aggregateLabel = (buckets) => {
  if (!buckets || buckets.length === 0) return "No data"
  const todayVal = buckets[buckets.length - 1]?.value
  if (todayVal === null || todayVal === undefined) return "No data"
  if (todayVal >= 0.999) return "Operational"
  if (todayVal >= 0.8) return "Degraded"
  return "Down"
}

const formatDate = (ts) => {
  const d = new Date(ts)
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString("en-US", sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" })
}

const formatPct = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : "—")

// Pad to full day range — KPI only returns days with data
const padToDays = (raw, days) => {
  const buckets = raw || []
  const padded = []
  const now = new Date()
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i)
    const ts = d.toISOString()
    const existing = buckets.find((b) => {
      const bt = new Date(b.timestamp)
      return bt.getFullYear() === d.getFullYear() && bt.getMonth() === d.getMonth() && bt.getDate() === d.getDate()
    })
    padded.push(existing || { timestamp: ts, value: null })
  }
  return padded
}

// Pass `buckets` when the caller already has day-granularity history
// (list_components / get_public_status responses) to avoid a duplicate
// read_component_history request.
const HealthGrid = ({ componentId, days = 90, compact, buckets: bucketsProp }) => {
  const [buckets, setBuckets] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (bucketsProp) {
      setBuckets(padToDays(bucketsProp, days))
      return
    }
    if (!componentId) return
    setLoading(true)
    readComponentHistory(componentId, { days, granularity: "day", tzOffsetMinutes: -new Date().getTimezoneOffset() })
      .then((r) => setBuckets(padToDays(r?.buckets, days)))
      .finally(() => setLoading(false))
  }, [componentId, days, bucketsProp])

  if (loading) return <div className="animate-pulse h-8 bg-neutral-200 rounded" />
  if (!buckets) return <div className="text-text_secondary text-sm">No history data</div>

  return (
    <div>
      <div className={`text-sm font-semibold mb-1 ${aggregateColor(buckets)}`}>
        {aggregateLabel(buckets)}
      </div>
      {buckets.length > 90 ? (
        <div
          className="grid gap-[2px]"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(12px, 1fr))" }}
        >
          {buckets.map((b, i) => (
            <div
              key={i}
              className={`aspect-square rounded-sm ${barColor(b?.value)}`}
              title={`${formatDate(b?.timestamp)} — ${formatDowntime(b?.value)}`}
            />
          ))}
        </div>
      ) : (
        <div className="flex items-stretch gap-px" style={{ height: compact ? 24 : 32 }}>
          {buckets.map((b, i) => (
            <div
              key={i}
              className={`flex-1 rounded-sm ${barColor(b?.value)}`}
              style={{ minWidth: 2 }}
              title={`${formatDate(b?.timestamp)} — ${formatDowntime(b?.value)}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default HealthGrid
